import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { date, money } from '../lib/format.js';
import { Modal } from '../components/Modal.jsx';

export default function Recurring() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['recurring'], queryFn: () => api('/api/recurring') });
  const [creating, setCreating] = useState(false);
  return (
    <>
      <div className="page-header">
        <h1>Recurring schedules</h1>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>New schedule</button>
      </div>
      <table className="table">
        <thead>
          <tr><th>Name</th><th>Interval</th><th>Next run</th><th>Auto-send</th><th>Active</th><th># generated</th></tr>
        </thead>
        <tbody>
          {data?.schedules?.map(s => (
            <tr key={s.id}>
              <td>{s.name}</td>
              <td>Every {s.everyN} {s.interval.toLowerCase()}</td>
              <td>{date(s.nextRunAt)}</td>
              <td>{s.autoSend ? '✓' : ''}</td>
              <td>{s.active ? 'Yes' : 'No'}</td>
              <td>{s._count?.documents ?? 0}</td>
            </tr>
          ))}
          {!data?.schedules?.length && <tr><td colSpan={6} className="empty">No schedules yet.</td></tr>}
        </tbody>
      </table>
      {creating && <RecurringModal onClose={() => setCreating(false)} onDone={() => { qc.invalidateQueries({ queryKey: ['recurring'] }); setCreating(false); }} />}
    </>
  );
}

function RecurringModal({ onClose, onDone }) {
  const { data: clients } = useQuery({ queryKey: ['clients-all'], queryFn: () => api('/api/clients') });
  const { data: articles } = useQuery({ queryKey: ['articles-all'], queryFn: () => api('/api/articles') });
  const [form, setForm] = useState({
    name: '', clientId: '', interval: 'MONTHLY', everyN: 1,
    nextRunAt: new Date().toISOString().slice(0, 10), autoSend: false,
    items: [{ articleId: '', sku: '', name: '', qty: 1, unit: 'pcs', unitPrice: 0, discountPct: 0, vatPct: 18 }],
  });
  const [busy, setBusy] = useState(false);
  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/api/recurring', { method: 'POST', body: {
        name: form.name, clientId: form.clientId, interval: form.interval, everyN: form.everyN,
        nextRunAt: form.nextRunAt, autoSend: form.autoSend,
        templateJson: { items: form.items, currencyCode: 'EUR' },
      }});
      onDone();
    } finally { setBusy(false); }
  };
  const pickArticle = (i, id) => {
    const a = articles?.articles?.find(x => x.id === id); if (!a) return;
    setForm(f => { const items = [...f.items]; items[i] = { ...items[i], articleId: a.id, sku: a.sku, name: a.name, unit: a.unit, unitPrice: Number(a.price), vatPct: a.vatRate ? Number(a.vatRate.rate) : 0 }; return { ...f, items }; });
  };
  return (
    <Modal title="New recurring schedule" onClose={onClose} wide>
      <form onSubmit={submit}>
        <div className="row">
          <div className="field"><label>Name</label><input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
          <div className="field"><label>Client</label>
            <select required value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}>
              <option value="">—</option>{clients?.clients?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div className="row">
          <div className="field"><label>Interval</label>
            <select value={form.interval} onChange={e => setForm(f => ({ ...f, interval: e.target.value }))}>
              {['WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY'].map(i => <option key={i}>{i}</option>)}
            </select>
          </div>
          <div className="field"><label>Every N</label><input type="number" min={1} value={form.everyN} onChange={e => setForm(f => ({ ...f, everyN: Number(e.target.value) }))} /></div>
          <div className="field"><label>Next run</label><input type="date" required value={form.nextRunAt} onChange={e => setForm(f => ({ ...f, nextRunAt: e.target.value }))} /></div>
          <div className="field"><label><input type="checkbox" checked={form.autoSend} onChange={e => setForm(f => ({ ...f, autoSend: e.target.checked }))} /> Auto-send</label></div>
        </div>
        <h3>Line items</h3>
        <table className="table">
          <thead><tr><th>Article</th><th>Name</th><th>Qty</th><th>Price</th><th>VAT %</th><th></th></tr></thead>
          <tbody>
            {form.items.map((it, i) => (
              <tr key={i}>
                <td><select value={it.articleId} onChange={e => pickArticle(i, e.target.value)}>
                  <option value="">—</option>{articles?.articles?.map(a => <option key={a.id} value={a.id}>{a.sku} · {a.name}</option>)}
                </select></td>
                <td><input value={it.name} onChange={e => setForm(f => { const c = [...f.items]; c[i] = { ...c[i], name: e.target.value }; return { ...f, items: c }; })} /></td>
                <td><input type="number" step="0.01" style={{ width: 70 }} value={it.qty} onChange={e => setForm(f => { const c = [...f.items]; c[i] = { ...c[i], qty: Number(e.target.value) }; return { ...f, items: c }; })} /></td>
                <td><input type="number" step="0.01" style={{ width: 90 }} value={it.unitPrice} onChange={e => setForm(f => { const c = [...f.items]; c[i] = { ...c[i], unitPrice: Number(e.target.value) }; return { ...f, items: c }; })} /></td>
                <td><input type="number" step="0.01" style={{ width: 70 }} value={it.vatPct} onChange={e => setForm(f => { const c = [...f.items]; c[i] = { ...c[i], vatPct: Number(e.target.value) }; return { ...f, items: c }; })} /></td>
                <td><button type="button" className="btn btn-danger btn-sm" onClick={() => setForm(f => ({ ...f, items: f.items.filter((_, j) => j !== i) }))}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setForm(f => ({ ...f, items: [...f.items, { articleId: '', sku: '', name: '', qty: 1, unit: 'pcs', unitPrice: 0, discountPct: 0, vatPct: 18 }] }))}>+ Line</button>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? <span className="spinner" /> : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
}
